import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { FadeIn, Eyebrow } from "@/components/Reveal";

const INTEL = [
  {
    id: "sambar-deer",
    name: "Sambar Deer",
    latin: "Rusa unicolor",
    img: "https://images.unsplash.com/photo-1756617964176-bfc4bfa79346?q=85&w=1600&auto=format&fit=crop",
    numbers: "Victoria holds Australia's largest wild sambar herd",
    breeding: "Year-round, peaking May–July · single calf",
    damage: "$91M+ a year nationally (all deer) — grazing, erosion, vehicle strikes",
    note: "The big one. Hard country, wary animals, and herds that compound fast when left alone.",
  },
  {
    id: "red-deer",
    name: "Red Deer",
    latin: "Cervus elaphus",
    img: "https://images.unsplash.com/photo-1601274016113-72b786d7d16e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHw0fHx3aWxkJTIwZGVlciUyMGZvcmVzdHxlbnwwfHx8fDE3ODg1ODYwMzB8MA&ixlib=rb-4.1.0&q=85",
    numbers: "Smaller, localised herds — Grampians & north-east VIC",
    breeding: "Autumn rut, March–April · single calf born in spring",
    damage: "Shares in the $91M+ national deer damage bill",
    note: "Classic roaring-stag country; the autumn rut brings stags out of the thick timber.",
  },
  {
    id: "fallow-deer",
    name: "Fallow Deer",
    latin: "Dama dama",
    img: "https://images.unsplash.com/photo-1537694513497-5f0b0ec361c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHx3aWxkJTIwZGVlciUyMGZvcmVzdHxlbnwwfHx8fDE3ODg1ODYwMzB8MA&ixlib=rb-4.1.0&q=85",
    numbers: "Widespread and expanding across VIC & southern NSW",
    breeding: "Autumn rut, April · single fawn born in spring",
    damage: "Crop raiding, pasture grazing and ring-barking of young trees",
    note: "Often the first deer landholders notice — and the quickest to colonise new country.",
  },
  {
    id: "hog-deer",
    name: "Hog Deer",
    latin: "Axis porcinus",
    img: "https://upload.wikimedia.org/wikipedia/commons/7/75/Axis_porcinus_406506618.jpg",
    numbers: "Coastal Gippsland — Australia's only wild population",
    breeding: "Year-round, peaking autumn · single fawn",
    damage: "Cropping and revegetation damage on coastal country",
    note: "Small, low-slung and built for thick coastal scrub — easy to underestimate.",
  },
  {
    id: "kangaroo",
    name: "Kangaroo",
    latin: "Macropus giganteus · Macropus fuliginosus",
    img: "https://images.unsplash.com/photo-1575699914911-0027c7b95fb6?q=85&w=1600&auto=format&fit=crop",
    numbers: "Tens of millions nationally — grey mobs build fast on improved pasture",
    breeding: "Year-round · one joey, with another embryo held ready to follow",
    damage: "Pasture competition with stock, fence damage and vehicle strikes",
    note: "Managed under ATCW authority where numbers impact production — positive ID, always.",
  },
  {
    id: "feral-pig",
    name: "Feral Pig",
    latin: "Sus scrofa",
    img: "https://images.unsplash.com/photo-1727701773949-69e701bdb680?q=85&w=1600&auto=format&fit=crop",
    numbers: "Up to ~24 million across Australia",
    breeding: "Year-round, peaking after rain · up to 2 litters a year · 6–10 piglets",
    damage: "$100M+ a year in crop, pasture & fencing losses",
    note: "Rooting rips up cultivation ground, spreads weeds and preys on newborn lambs.",
  },
  {
    id: "european-fox",
    name: "European Red Fox",
    latin: "Vulpes vulpes",
    img: "https://images.unsplash.com/photo-1516934024742-b461fba47600?q=85&w=1600&auto=format&fit=crop",
    numbers: "~7.2 million spread across most of Australia",
    breeding: "Mating June–July · cubs born Aug–Sep · 4–5 per litter",
    damage: "$227M+ a year in lamb losses and control costs",
    note: "A single fox working a lambing paddock can take a lamb a night through the season.",
  },
  {
    id: "european-rabbit",
    name: "European Rabbit",
    latin: "Oryctolagus cuniculus",
    img: "https://images.unsplash.com/photo-1480554840075-72cbdabbf689?q=85&w=1600&auto=format&fit=crop",
    numbers: "~200 million in good seasons",
    breeding: "Mainly spring–autumn, after rain · 5+ litters a year · 4–6 kittens",
    damage: "$200M+ a year in lost production",
    note: "Rabbits strip pasture to the roots and undermine tracks, sheds and dam walls.",
  },
  {
    id: "hare",
    name: "European Hare",
    latin: "Lepus europaeus",
    img: "https://upload.wikimedia.org/wikipedia/commons/3/3a/Bingenheimer_Ried_Hase-01_2019-02-24.jpg",
    numbers: "Widespread across VIC & southern NSW pasture country",
    breeding: "Year-round, peaking spring–autumn · 2–4 leverets per litter",
    damage: "Pasture competition and cropping damage — hares graze like small sheep",
    note: "Explosive speed across open paddocks — a spotlighting staple on night runs.",
  },
  {
    id: "wild-dog",
    name: "Wild Dog & Dingo",
    latin: "Canis familiaris",
    img: "https://images.unsplash.com/photo-1760128078930-e9ec95c4b905?q=85&w=1600&auto=format&fit=crop",
    numbers: "Widespread through the ranges of eastern VIC & southern NSW",
    breeding: "Autumn–winter breeding, March–June · 4–6 pups",
    damage: "~$110M a year in stock losses nationally",
    note: "Wild dogs harass, maim and kill stock — often without feeding on the carcass.",
  },
  {
    id: "feral-goat",
    name: "Feral Goat",
    latin: "Capra hircus",
    img: "https://images.unsplash.com/photo-1588466585717-f8041aec7875?q=85&w=1600&auto=format&fit=crop",
    numbers: "~2.3 million nationally",
    breeding: "Peak mating autumn · up to 2 kids a year",
    damage: "$25M+ a year — overgrazing and erosion",
    note: "Mobs strip ground cover bare and open country up to weeds and erosion.",
  },
  {
    id: "feral-cat",
    name: "Feral Cat",
    latin: "Felis catus",
    img: "https://images.unsplash.com/photo-1515002246390-7bf7e8f87b54?q=85&w=1600&auto=format&fit=crop",
    numbers: "1.4–5.6 million nationally, depending on season",
    breeding: "Spring–summer peak · up to 2 litters a year · 4–6 kittens",
    damage: "~2 billion native animals killed each year",
    note: "The silent pressure on ground-nesting birds and small native mammals.",
  },
];

export default function Gallery() {
  return (
    <main data-testid="gallery-page" className="pt-16">
      <section className="mx-auto max-w-7xl px-4 pb-12 pt-16 sm:px-6 lg:px-8">
        <FadeIn>
          <Eyebrow>Field Work · Pest Intelligence</Eyebrow>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-bone sm:text-5xl lg:text-6xl">
            Know the target.
            <br />
            <span className="text-thermal">Know the cost.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-euca sm:text-lg">
            A field guide to the vertebrate pest and wild game animals that damage
            pasture, crops, livestock and native habitat right across the country — and
            how quickly their numbers build when they're left unchecked.
          </p>
        </FadeIn>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 lg:px-8">
        <div className="grid gap-4 md:grid-cols-2" data-testid="pest-intel-grid">
          {INTEL.map((p, i) => (
            <FadeIn key={p.id} delay={(i % 2) * 0.1}>
              <article
                id={p.id}
                data-testid={`pest-card-${p.id}`}
                className="group h-full scroll-mt-24 border border-thermal/15 bg-field transition-colors duration-500 hover:border-thermal/50"
              >
                <div className="relative aspect-[16/8] overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.name}
                    className="h-full w-full object-cover grayscale-[0.6] transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                  />
                  <span className="absolute left-4 top-4 bg-obsidian/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-thermal backdrop-blur">
                    {p.latin}
                  </span>
                </div>
                <div className="p-6 sm:p-8">
                  <h2 className="font-display text-xl font-semibold tracking-tight text-bone sm:text-2xl">{p.name}</h2>
                  <dl className="mt-6 space-y-4">
                    {[
                      ["Est. Numbers", p.numbers],
                      ["Breeding Cycle", p.breeding],
                      ["Annual Damage", p.damage],
                    ].map(([label, value]) => (
                      <div
                        key={label}
                        className="flex flex-col gap-1 border-t border-thermal/10 pt-3 sm:flex-row sm:items-baseline sm:justify-between"
                      >
                        <dt className="font-mono text-[10px] uppercase tracking-[0.25em] text-euca">{label}</dt>
                        <dd className="text-sm font-medium text-bone/90 sm:text-right">{value}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="mt-6 text-sm leading-relaxed text-euca">{p.note}</p>
                </div>
              </article>
            </FadeIn>
          ))}
        </div>

        <FadeIn className="mt-12">
          <div className="border border-thermal/15 bg-field p-8" data-testid="records-privacy-note">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-thermal">A Note On Our Records</p>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-bone/90">
              Client field records, tallies and imagery are private — shared only with the
              landholder, in writing, after every job. The population and damage figures above
              are national estimates drawn from published government and industry sources; the
              pressure on your ground is its own number. A property assessment puts a real
              figure on it.
            </p>
            <Link
              to="/calculator"
              data-testid="pest-intel-calculator-link"
              className="group mt-6 inline-flex items-center gap-2 border border-thermal px-6 py-3 font-mono text-xs uppercase tracking-[0.2em] text-thermal transition-all duration-300 hover:bg-thermal hover:text-obsidian active:scale-95"
            >
              Scope Your Property
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </FadeIn>
      </section>
    </main>
  );
}
